import { SelectChangeEvent } from '@mui/material'
import { OptJobStatus } from 'api/optJobs/optJobs.api'
import { postStat } from 'api/stats/stats.api'
import { t } from 'i18next'
import moment from 'moment'
import React, { ReactElement, useState } from 'react'
import { colors } from 'styles/variables'
import Icon from 'ui/atoms/Icon/Icon'
import StatusIcon from 'ui/atoms/StatusIcon/StatusIcon'
import Dropdown from 'ui/components/Dropdown/Dropdown'
import {
  getOptJobTypeTagStyle,
  OptJobAndQueueItemWithType,
  optJobStatusTranslationMapping,
  optJobTypeTranslationMapping,
} from './Optimizations.helper'

type OptimizationsDropdownProps = {
  onSelect?: (item: OptJobAndQueueItemWithType | null) => void
  latestOptJobsAndQueueItems?: OptJobAndQueueItemWithType[]
  isFetchingItems: 'idle' | 'pending' | 'success' | 'error'
}

const optJobIdQueueItemIdSeparator = '_'

export const getStatusIcon = (status?: OptJobStatus): React.ReactNode => {
  switch (status) {
    case 'Created':
      return <StatusIcon variant="regular_created" size="small" tooltip={{ title: t('Created') }} />
    case 'Queued':
      return <StatusIcon variant="queued" size="small" tooltip={{ title: t('Queued') }} />
    case 'Running':
      return <StatusIcon variant="spinner" size="small" tooltip={{ title: t('Running') }} />
    case 'Failed':
      return <StatusIcon variant="regular_failed" size="small" tooltip={{ title: t('Failed') }} />
    case 'Finished':
      return <StatusIcon variant="regular_finished" size="small" tooltip={{ title: t('Finished') }} />
    case 'Unsolvable':
      return <StatusIcon variant="regular_unsolvable" size="small" tooltip={{ title: t('Unsolvable') }} />
    default:
      return <StatusIcon variant="spinner" size="small" tooltip={{ title: '' }} />
  }
}

export default function OptimizationsDropdown({
  onSelect,
  latestOptJobsAndQueueItems,
  isFetchingItems,
}: OptimizationsDropdownProps): ReactElement {
  const [dropdownValue, setDropdownValue] = useState<string>('')

  const handleDropdownChange = (event: SelectChangeEvent<string>): void => {
    setDropdownValue(event.target.value as string)

    const [optJobId, queueItemId] = event.target.value
      .split(optJobIdQueueItemIdSeparator)
      .map((val) => (isNaN(Number(val)) ? null : Number(val)))

    postStat('review_optimizations', `selected-opt-job-id-${optJobId}`)

    const selectedOptJobAndQueueItem = latestOptJobsAndQueueItems?.find(
      (item) => item.opt_job_id === optJobId && item.queue_item_id === queueItemId
    )

    onSelect?.(selectedOptJobAndQueueItem || null)
  }

  const dropdownItems: { value: string; label: React.ReactNode }[] = (latestOptJobsAndQueueItems ?? []).map(
    (flattenedOptJobAndQueueItem: OptJobAndQueueItemWithType) => {
      const iconNode = getStatusIcon(flattenedOptJobAndQueueItem.opt_job_status ?? undefined)

      const beforeTypeText = `Id: ${flattenedOptJobAndQueueItem.opt_job_id} | ${t('Opt job type')}: `
      const afterTypeText =
        ` | ${t('Created at')}: ${moment(flattenedOptJobAndQueueItem.opt_job_created_at).format('YYYY-MM-DD HH:mm')} | ` +
        `${t('Updated at')}: ${moment(flattenedOptJobAndQueueItem.opt_job_updated_at).format('YYYY-MM-DD HH:mm')} | ` +
        `${t('Status')}: ${optJobStatusTranslationMapping(flattenedOptJobAndQueueItem.opt_job_status ?? undefined)}` +
        (flattenedOptJobAndQueueItem.queue_item_subtype
          ? ` | ${t('Opt job sub type')}: ${flattenedOptJobAndQueueItem.queue_item_subtype}`
          : '')

      return {
        value:
          `${flattenedOptJobAndQueueItem.opt_job_id}` +
          `${optJobIdQueueItemIdSeparator}` +
          `${flattenedOptJobAndQueueItem.queue_item_id}`,
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {iconNode}
            <span style={{ marginLeft: iconNode ? 8 : 0 }}>
              {beforeTypeText}
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${getOptJobTypeTagStyle(flattenedOptJobAndQueueItem.opt_job_type)}`}
              >
                {optJobTypeTranslationMapping(flattenedOptJobAndQueueItem.opt_job_type)}
              </span>
              {afterTypeText}
            </span>
          </div>
        ),
      }
    }
  )

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }} className="choose_optimization">
      {isFetchingItems === 'pending' && <Icon size="large" icon="fas fa-spinner fa-spin" color="blue" />}
      <Dropdown
        label={
          isFetchingItems === 'pending'
            ? `${t('Fetching optimization jobs')}...`
            : t('Select an optimization to review')
        }
        style={{ backgroundColor: colors.white }}
        disabled={isFetchingItems === 'pending'}
        items={dropdownItems}
        value={dropdownValue}
        handleChange={handleDropdownChange}
        fullWidth
        margin="dense"
      />
    </div>
  )
}
